import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Header,
  Card,
  Icon,
  Label,
  Segment,
  Message,
  Statistic,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, getAuthHeaders } from '../api';

const CATEGORIES = [
  {
    key: 'policies',
    title: 'Policies',
    description: 'Governance policies and commitments',
    icon: 'book',
    color: 'green',
    types: ['POL'],
    route: '/policies',
    dedicated: true,
  },
  {
    key: 'procedures',
    title: 'Procedures',
    description: 'Standard operating procedures',
    icon: 'tasks',
    color: 'blue',
    types: ['SOP'],
  },
  {
    key: 'work-instructions',
    title: 'Work Instructions',
    description: 'Step-by-step guides and how-to documents',
    icon: 'wrench',
    color: 'teal',
    types: ['WI'],
  },
  {
    key: 'design-records',
    title: 'Design Records',
    description: 'Technical design decisions and implementation records',
    icon: 'pencil',
    color: 'purple',
    types: ['DR'],
  },
  {
    key: 'bugs',
    title: 'Bug Reports',
    description: 'Problem reports and defect tracking',
    icon: 'bug',
    color: 'red',
    types: ['BUG'],
    route: '/qms/issues',
    dedicated: true,
  },
  {
    key: 'security',
    title: 'Security Assessments',
    description: 'Security analyses, incident reports, and threat assessments',
    icon: 'shield',
    color: 'orange',
    types: ['SA'],
  },
  {
    key: 'change-records',
    title: 'Change Records',
    description: 'Controlled change requests and approvals',
    icon: 'exchange',
    color: 'yellow',
    types: ['CR'],
  },
  {
    key: 'capa',
    title: 'CAPAs',
    description: 'Corrective and preventive actions',
    icon: 'medkit',
    color: 'red',
    types: ['CAPA'],
  },
  {
    key: 'technical',
    title: 'Technical Documents',
    description: 'Architecture, references, guides, and technical notes',
    icon: 'code',
    color: 'grey',
    types: ['TECH', 'ARCH', 'REF', 'GUIDE'],
  },
  {
    key: 'records',
    title: 'Records & Evidence',
    description: 'Forms, evidence, meeting records',
    icon: 'folder',
    color: 'brown',
    types: ['REC', 'FRM', 'EVD'],
  },
];

export default function DocumentHub() {
  const [counts, setCounts] = useState({});
  const [totalDocs, setTotalDocs] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchCounts() {
      try {
        const res = await fetch(
          `${getApiPath()}/@polyglot/document-hub`,
          { headers: getAuthHeaders() },
        );
        if (res.ok) {
          const data = await res.json();
          setCounts(data.counts || {});
          setTotalDocs(data.total || 0);
        }
      } catch {
        // non-critical
      }
      setLoading(false);
    }
    fetchCounts();
  }, []);

  const getCount = (types) => {
    return types.reduce((sum, t) => sum + (counts[t] || 0), 0);
  };

  const getLastUpdated = (types) => {
    const dates = types
      .map((t) => counts[`${t}_updated`])
      .filter(Boolean);
    if (dates.length === 0) return null;
    return new Date(Math.max(...dates.map((d) => new Date(d).getTime())));
  };

  return (
    <Container style={{ paddingTop: '2em' }}>
      <Helmet title="Documents" />
      <div className="polyglot-page-header">
        <h1>Document Management</h1>
        <p className="subtitle">
          {loading ? 'Loading...' : `${totalDocs} documents across all categories`}
        </p>
      </div>

      <Card.Group itemsPerRow={3} stackable>
        {CATEGORIES.map((cat) => {
          const count = getCount(cat.types);
          if (!loading && count === 0) return null; // Hide empty categories (prevents metadata leak for restricted users)
          const lastUpdated = getLastUpdated(cat.types);
          const route = cat.dedicated
            ? cat.route
            : `/docs?document_type=${cat.types.join(',')}`;

          return (
            <Card key={cat.key} as={Link} to={route}>
              <Card.Content>
                <Icon
                  name={cat.icon}
                  size="big"
                  color={cat.color}
                  style={{ float: 'right' }}
                />
                <Card.Header>{cat.title}</Card.Header>
                <Card.Meta>{cat.description}</Card.Meta>
              </Card.Content>
              <Card.Content>
                <Statistic size="mini">
                  <Statistic.Value>{loading ? '–' : count}</Statistic.Value>
                  <Statistic.Label>documents</Statistic.Label>
                </Statistic>
              </Card.Content>
              <Card.Content extra>
                {lastUpdated && (
                  <span style={{ color: '#999' }}>
                    <Icon name="clock" />
                    Updated: {lastUpdated.toLocaleDateString()}
                  </span>
                )}
                {cat.dedicated && (
                  <Label size="tiny" color={cat.color} style={{ float: 'right' }}>
                    Dedicated view
                  </Label>
                )}
              </Card.Content>
            </Card>
          );
        })}
      </Card.Group>
    </Container>
  );
}
